import { defineStore } from 'pinia'
import notificationsApi from '@/api/notificationsApi.js'

// Глобальный callback для toast (устанавливается из App.vue)
let toastCallback = null

export function setNotificationToastCallback(callback) {
  toastCallback = callback
}

export const useNotificationsStore = defineStore('notificationsStore', {
  state: () => ({
    items: [],
    unreadCount: 0,
    loading: false,
    hasMore: true,
    offset: 0
  }),

  getters: {
    unreadNotifications: (state) => state.items.filter(n => !n.is_read),
    notClosedNotifications: (state) => state.items.filter(n => !n.is_closed)
  },

  actions: {
    async loadNotifications(reset = true) {
      if (this.loading) return

      this.loading = true
      try {
        if (reset) {
          this.offset = 0
          this.items = []
        }

        const data = await notificationsApi.getNotifications({
          limit: 20,
          offset: this.offset,
          unread_only: true,  // Загружаем только непрочитанные
          not_closed_only: true
        })

        if (reset) {
          this.items = data.results || []
        } else {
          this.items.push(...(data.results || []))
        }

        this.unreadCount = data.unread_count || 0
        this.hasMore = data.has_more || false
        this.offset += data.results?.length || 0
      } catch (error) {
        console.error('Error loading notifications:', error)
      } finally {
        this.loading = false
      }
    },

    async loadMore() {
      if (!this.hasMore || this.loading) return
      await this.loadNotifications(false)
    },

    async markAsRead(id) {
      try {
        await notificationsApi.markAsRead(id)
        // Удаляем уведомление из списка после прочтения
        const index = this.items.findIndex(n => n.id === id)
        if (index !== -1) {
          const notification = this.items[index]
          if (!notification.is_read) {
            this.unreadCount = Math.max(0, this.unreadCount - 1)
          }
          this.items.splice(index, 1)
        }
      } catch (error) {
        console.error('Error marking notification as read:', error)
      }
    },

    async markAllAsRead() {
      try {
        await notificationsApi.markAllAsRead()
        // Удаляем все уведомления из списка после прочтения
        this.items = []
        this.unreadCount = 0
      } catch (error) {
        console.error('Error marking all notifications as read:', error)
      }
    },

    async closeNotification(id) {
      try {
        await notificationsApi.closeNotification(id)
        const index = this.items.findIndex(n => n.id === id)
        if (index !== -1) {
          const notification = this.items[index]
          if (!notification.is_read) {
            this.unreadCount = Math.max(0, this.unreadCount - 1)
          }
          this.items.splice(index, 1)
        }
      } catch (error) {
        console.error('Error closing notification:', error)
      }
    },

    // WebSocket event handlers
    applyWsEvent(payload) {
      if (payload.type !== 'notification') return

      switch (payload.event) {
        case 'created':
          this.handleNotificationCreated(payload.data)
          break
        case 'read':
          this.handleNotificationRead(payload.data)
          break
        case 'closed':
          this.handleNotificationClosed(payload.data)
          break
        case 'mark_all_read':
          this.handleMarkAllRead()
          break
      }
    },

    handleNotificationCreated(notification) {
      // Добавляем новое уведомление в начало списка
      this.items.unshift(notification)
      if (!notification.is_read) {
        this.unreadCount++
      }
      
      // Показываем toast только один раз
      if (toastCallback) {
        toastCallback(notification)
      }
    },

    handleNotificationRead(data) {
      // Удаляем уведомление из списка при прочтении (через WS)
      const index = this.items.findIndex(n => n.id === data.id)
      if (index !== -1) {
        const notification = this.items[index]
        if (!notification.is_read) {
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        }
        this.items.splice(index, 1)
      }
    },

    handleNotificationClosed(data) {
      const index = this.items.findIndex(n => n.id === data.id)
      if (index !== -1) {
        const notification = this.items[index]
        if (!notification.is_read) {
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        }
        this.items.splice(index, 1)
      }
    },

    handleMarkAllRead() {
      // Удаляем все уведомления при "отметить все как прочитанные"
      this.items = []
      this.unreadCount = 0
    }
  }
})
